import type { WeightSampleLog } from '../../types/production'
import type { BiomassDataRow } from '../../utils/productionUnitTable'

import { normalizeToIsoZMidnight } from '../../utils/dateNormalization'
import { createId } from '../../utils/id'
import { buildBiomassSamplingWeightMetadata } from '../biomass/weightSampleLogBridge'

export type WeightSampleSynthesisContext = {
  farmId: string
  productionUnitId: string
}

const MAX_WEIGHT_GRAMS = 10_000

const parseWeightValue = (value: unknown): number | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return null
  if (parsed <= 0 || parsed > MAX_WEIGHT_GRAMS) return null
  return parsed
}

const trimmedOrUndefined = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

/**
 * Synthesize `WeightSampleLog` events from CSV-imported production rows.
 *
 * Used by the production-summary import path in `ImportWorkspace` so
 * imported `row.weight` values persist as authoritative WeightSampleLog
 * events. Post-T3-A-07b (2026-07-06) the paired
 * `ProductionRecord.weightGrams` cache column is retired and no longer
 * accepted by `toRecordPayload`; the overlay chain
 * (`overlayWeightSamplesOnProductionRows`) is the only surface that can
 * render the imported value after the next `refreshProductionUnitTableFromApi`
 * pull. Without this synthesis the imported weight strings live only in
 * localStorage and vanish on the first API refresh.
 *
 * Skip rules (produce no event for a row):
 * - `row.weight` is not a string, is blank, non-numeric, ≤ 0, or > 10,000 g.
 * - `row.date` cannot be normalized to an ISO Z instant.
 * - The synthesis context is missing `farmId` or `productionUnitId`.
 *
 * The caller is responsible for filtering `rows` down to *imported* rows
 * only (e.g. via `savePlan.syncRowIds`) — this helper does not distinguish
 * imported rows from pre-existing rows already in the table.
 */
export const synthesizeWeightSampleLogsFromImportedRows = (
  rows: BiomassDataRow[],
  context: WeightSampleSynthesisContext
): WeightSampleLog[] => {
  const farmId = typeof context.farmId === 'string' ? context.farmId.trim() : ''
  const productionUnitId = typeof context.productionUnitId === 'string'
    ? context.productionUnitId.trim()
    : ''
  if (!farmId || !productionUnitId) return []

  const importedAt = new Date().toISOString()
  const logs: WeightSampleLog[] = []

  for (const row of rows) {
    const weightGrams = parseWeightValue(row.weight)
    if (weightGrams === null) continue

    const normalizedDate = normalizeToIsoZMidnight(typeof row.date === 'string' ? row.date : '')
    if (!normalizedDate.ok) continue

    const productionRowId = String(row.id)
    const lotId = trimmedOrUndefined(row.lotId)
    const stage: 'Active' | 'Inactive' = row.stage === 'Inactive' ? 'Inactive' : 'Active'

    logs.push({
      id: createId('wsl'),
      farmId,
      productionUnitId,
      lot: trimmedOrUndefined(row.lot),
      // A1.1 Fix A: `lotId` belongs on the top-level column as well as in
      // metadata — `lotMatches` and `LotStoryService` read the column, so a
      // metadata-only lotId drops imported samples from the lot story once
      // the lot is transferred.
      lotId,
      productionRowId,
      recordedAt: normalizedDate.isoZ,
      weightGrams: String(weightGrams),
      notes: trimmedOrUndefined(row.notes),
      metadata: {
        ...buildBiomassSamplingWeightMetadata({
          productionRowId,
          lotId: lotId ?? '',
          stage
        }),
        source: 'csvImport',
        productionRowId,
        lotId,
        importedAt
      }
    })
  }

  return logs
}
